import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import useSocket from '../../hooks/useSocket';
import './styles.scss';

type DynamicLeagueEventUIProps = {
  controlActive: boolean;
  deviceId: string;
  isDeviceConnected: boolean;
  leagueId: string;
};

type v3Command = {
  tag: string;
}

type control = {
  disabled: boolean;
  title: string;
  color?: string;
  command: v3Command | string;
};

const DynamicLeagueEventUI = forwardRef(({ controlActive, deviceId, isDeviceConnected, leagueId }: DynamicLeagueEventUIProps, ref) => {
  const [controls, setControls] = React.useState<control[][]>([]);
  const [hasLoadedUI, setHasLoadedUI] = React.useState(false);
  const [deviceVersion, setDeviceVersion] = useState(null);
  const { isConnected, send, subscribe } = useSocket();

  const requestUI = () => {
    const command = {
      globalUIRequest: 'globalUIRequest'
    };

    send(JSON.stringify({
      channel: 'command',
      metadata: {
        version: 'v3.0',
        ID: 0
      },
      payload: {
        deviceId: deviceId,
        command: command
      }
    }));
  };

  const ControlButtonItem = ({ disabled, title, color, command }: control) => {
    const { send } = useSocket();

    const sendCommand = () => {
      if (deviceVersion === 'v2.0') {
        send(JSON.stringify({
          channel: 'command',
          metadata: {
            version: deviceVersion,
            ID: 0
          },
          payload: {
            deviceId: deviceId,
            command: command
          }
        }))
      }
      else if (deviceVersion === 'v3.0') {
        const composedCommand = {} as any;

        composedCommand[(command as v3Command).tag] = (command as v3Command).tag;

        send(JSON.stringify({
          channel: 'command',
          metadata: {
            version: deviceVersion,
            ID: 0
          },
          payload: {
            leagueId: leagueId,
            command: {
              ...composedCommand
            }
          }
        }))
      }
    };

    return (
      <div className='control-button-wrapper'>
        <button className={`${disabled && 'control-button-disabled'}`} disabled={disabled} style={{backgroundColor: color}} onClick={() => sendCommand()}>{title}</button>
      </div>
    );
  }

  const ControlButtonSpacer = () => (
    <div className={'control-button-wrapper'}></div>
  );

  const renderControlButtons = (controls: control[][]) => {
    return controls.map((controlRow, row) =>
      <div className={'control-button-container-row'} key={row}>
        {
          controlRow.map((control, col) => {
            if (control) {
              return <ControlButtonItem disabled={!controlActive} key={row + col + 1} title={control.title} color={control.color} command={control.command}/>;
            } else {
              return <ControlButtonSpacer/>;
            }
          })
        }
      </div>
    );
  }

  useEffect(() => {
    if(deviceId && isConnected) {
      const unsubscribe = subscribe(`gameUI-device-${deviceId}-response`, (message) => {
        if (!hasLoadedUI) {
          setHasLoadedUI(true);
        }

        setDeviceVersion(message?.metadata?.version);
        setControls(message?.payload?.UI);
      });

      setTimeout(() => {
        requestUI();
      }, 100)

      return unsubscribe;
    }
  }, [deviceId, isConnected]);

  useEffect(() => {
    if(isConnected) {
      requestUI();
    }
  }, [isConnected]);

  useEffect(() => {
    if(!isDeviceConnected) {
      setControls([]);
    }
  }, [isDeviceConnected]);

  useImperativeHandle(ref, () => ({
    requestUI,
  }));

  return (
    <div className='dynamic-ui-controller'>
      {
        controls.length > 0 ?
        renderControlButtons(controls) :
        <p>Waiting for controls from game...</p>
      }
    </div>
  )
});


export default DynamicLeagueEventUI;
